import queryString, { ParseOptions, ParsedQuery } from 'query-string';
import { NavigateOptions, useLocation, useNavigate } from 'react-router';

export interface QueryParamsType {
  page?: number | null;
  limit?: number | null;
  ordering?: string;
}

export const SEARCH_PARAMS_ARRAY_FORMAT_OPTIONS: ParseOptions = {
  arrayFormat: 'bracket-separator',
  arrayFormatSeparator: ',',
};

export const createSearchParams = (values: object) => {
  return queryString.stringify(values, {
    skipEmptyString: true,
    skipNull: true,
    ...SEARCH_PARAMS_ARRAY_FORMAT_OPTIONS,
  });
};

export const useReplaceSearchParams = <T>() => {
  const navigate = useNavigate();
  const { search: currentSearch, pathname, state } = useLocation();

  const getSearchParams = <U extends ParsedQuery<string>>() => {
    try {
      const searchParams = queryString.parse(currentSearch, {
        ...SEARCH_PARAMS_ARRAY_FORMAT_OPTIONS,
      });

      return searchParams as U;
    } catch (error) {
      console.error('Parsing search params failed with', error?.message);

      return {} as U;
    }
  };

  const replaceSearchParams = (
    newSearchParams: QueryParamsType & Partial<T>,
    navigateOptions: NavigateOptions = {}
  ) => {
    if (!newSearchParams) return;

    const searchParams = getSearchParams();
    const search = createSearchParams({ ...searchParams, ...newSearchParams });

    navigate({ pathname, search }, { state, ...navigateOptions });
  };

  const resetSearchParams = (navigateOptions: NavigateOptions = {}) => {
    navigate({ pathname, search: '' }, { state, ...navigateOptions });
  };

  return {
    getSearchParams,
    replaceSearchParams,
    resetSearchParams,
  };
};
